import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination } from "swiper/modules";
import styled, { keyframes } from "styled-components";

const scrollUp = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-50%);
  }
`;

const MarqueeContainer = styled.div`
  overflow: hidden;
  height: 100%;
  width: 100%;
  position: relative;
  display: block;
`;

const MarqueeTrack = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  animation: ${scrollUp} ${(props) => props.$duration || 30}s linear infinite;
  will-change: transform;

  &:hover {
    animation-play-state: paused;
  }
`;

const MarqueeItem = styled.div`
  flex-shrink: 0;
  width: 100%;
  padding: 0;
  box-sizing: border-box;
`;

/**
 * ULTIMATE SIMPLE SOLUTION
 * - No polling (disabled in parent components)
 * - No stabilization (items are frozen on mount)
 * - Just renders items directly
 * Desktop: continuous vertical marquee
 * Mobile: horizontal swiper (manual control)
 */
const PropertySwiper = React.memo(({
  items,
  renderItem,
  className,
  userType = "demand",
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 993);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 993);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!items || items.length === 0) {
    return null;
  }

  // Mobile: horizontal Swiper (manual control, no autoplay)
  if (isMobile) {
    return (
      <Swiper
        direction="horizontal"
        spaceBetween={12}
        slidesPerView={1}
        centeredSlides={false}
        speed={800}
        modules={[Pagination]}
        loop={items.length > 1}
        className={className}
        style={{ height: "100%", width: "100%" }}
        allowTouchMove={true}
        simulateTouch={true}
      >
        {items.map((item, index) => (
          <SwiperSlide
            key={
              item.isSponsor
                ? `sponsor-${index}`
                : `${userType}-${item.id ?? item.propertyId ?? item.displayId ?? index}`
            }
          >
            {renderItem(item, index)}
          </SwiperSlide>
        ))}
      </Swiper>
    );
  }

  // Desktop: continuous vertical marquee
  const duration = Math.max(items.length * 4, 15);

  return (
    <MarqueeContainer className={className}>
      <MarqueeTrack $duration={duration}>
        {/* First copy */}
        {items.map((item, index) => (
          <MarqueeItem
            key={
              item.isSponsor
                ? `sponsor-a-${index}`
                : `${userType}-a-${item.id ?? item.propertyId ?? item.displayId ?? index}`
            }
          >
            {renderItem(item, index)}
          </MarqueeItem>
        ))}
        {/* Second copy for seamless loop */}
        {items.map((item, index) => (
          <MarqueeItem
            key={
              item.isSponsor
                ? `sponsor-b-${index}`
                : `${userType}-b-${item.id ?? item.propertyId ?? item.displayId ?? index}`
            }
          >
            {renderItem(item, index)}
          </MarqueeItem>
        ))}
      </MarqueeTrack>
    </MarqueeContainer>
  );
});

PropertySwiper.displayName = 'PropertySwiper';

export default PropertySwiper;
